/**
 * Filosofi — profil socio-démographique du carreau de 200 m autour de la
 * parcelle.
 *
 * Filosofi (Fichier Localisé Social et Fiscal) est la base de données
 * fiscale de référence de l'INSEE, agrégée par carreaux de 200 m
 * (en zone urbaine) ou 1 km (en zone rurale, pour respecter le secret
 * statistique). Indicateurs disponibles :
 *
 *   - Niveau de vie moyen par individu (€/an, après prestations).
 *   - Taux de pauvreté (% de ménages sous le seuil de pauvreté monétaire).
 *   - Démographie par tranche d'âge (% < 18 ans, % 65+).
 *   - Composition des ménages (familles monoparentales, taille moyenne).
 *   - Statut d'occupation (% propriétaires, % logements sociaux).
 *   - Type de logement (% maisons vs. appartements, ancienneté).
 *
 * Source : Géoplateforme WFS, couche `INSEE.FILOSOFI.INDICATORS:carreaux_200m`.
 * CORS ouvert, anonyme. Champs documentés sur :
 *   https://www.insee.fr/fr/statistiques/4176290
 *
 * Stratégie de requête : on cible le carreau qui contient le point
 * geocode via un CQL `INTERSECTS` (un seul carreau retourné). Si pas
 * de carreau (zone non couverte / parcelle hors carreaux peuplés) :
 * fallback sur la couche 1 km. Si toujours rien : on remonte
 * l'absence proprement (la card affiche un message neutre).
 *
 * Pourquoi pas un callable backend ? Initialement prévu, mais la
 * Géoplateforme expose Filosofi en WFS public CORS-open — pas besoin
 * d'auth, pas besoin de proxy. On reste sur le même pattern client
 * que les autres cartes env.
 *
 * TTL : 180 jours. Le millésime Filosofi sort une fois par an avec
 * deux ans de décalage (publication 2024 = données 2021). Aucune
 * urgence de refetch fréquent.
 *
 * Secret statistique : INSEE marque `i_car_est=1` quand les valeurs
 * sont MODÉLISÉES (population trop faible pour publier les chiffres
 * réels). On surface l'info pour ne pas vendre du signal qui n'en est
 * pas un sur les hameaux peu peuplés.
 */

import { useMemo } from "react";
import { useEnvBlock, coordKey } from "./shared";
import type { FicheDoc } from "../fiches";

const WFS_ENDPOINT = "https://data.geopf.fr/wfs/ows";
const FILOSOFI_TTL_DAYS = 180;

// ─── Types ───────────────────────────────────────────────────────────────

export type FilosofiScale = "200m" | "1km";

export type FilosofiData = {
  // Identifiant INSPIRE du carreau (CRS3035RES200mN….E…). Permet une
  // dédup éventuelle et figure dans le footer pour transparence.
  idInspire: string;
  // Maille du carreau retenue (200 m sauf fallback rural).
  scale: FilosofiScale;
  // Code commune du carreau (depcom). Utile quand le carreau couvre une
  // commune limitrophe à celle de la parcelle.
  depcom: string;
  // Nom commune (libellé court).
  nomCommune: string;
  // True si les valeurs sont estimées par modélisation (secret stat
  // INSEE — la zone est trop peu peuplée pour publier les chiffres réels).
  estimated: boolean;
  // Population et ménages
  individus: number;
  menages: number;
  // Niveau de vie moyen par individu (€/an, après transferts sociaux).
  // Référence France métropolitaine : ~25 000 €/an (médiane ~23 000 €).
  niveauDeVieMoyen: number | null;
  // Taux de pauvreté monétaire (% de ménages sous le seuil).
  // Référence France métropolitaine : ~14,5 %.
  tauxPauvrete: number | null;
  // Démographie : % moins de 18 ans, % 65 ans et plus.
  partMoins18: number | null;
  part65plus: number | null;
  // Composition logement : % de maisons (vs. appartements), % ménages
  // propriétaires (vs. locataires), % de logements sociaux dans les
  // logements occupés.
  partMaisons: number | null;
  partProprietaires: number | null;
  partLogementsSociaux: number | null;
  // Ancienneté du parc : % construits avant 1945, après 1990.
  partAvant1945: number | null;
  partApres1990: number | null;
  // Familles monoparentales (% des ménages).
  partMonoparentales: number | null;
  // Surface moyenne des logements (m²).
  surfaceMoyenne: number | null;
};

// ─── Fetcher ─────────────────────────────────────────────────────────────

type WfsFeature = {
  type: "Feature";
  properties: Record<string, unknown>;
};
type WfsResponse = {
  type: "FeatureCollection";
  features: WfsFeature[];
};

async function queryFilosofi(
  scale: FilosofiScale,
  lat: number,
  lng: number,
): Promise<FilosofiData | null> {
  const layer =
    scale === "200m"
      ? "INSEE.FILOSOFI.INDICATORS:carreaux_200m"
      : "INSEE.FILOSOFI.INDICATORS:carreaux_1km";
  // EWKT avec SRID=4326 — c'est la seule forme qui marche fiable sur
  // ce serveur (BBOX et POINT 4326 sans SRID retournent toujours 0).
  const cql = `INTERSECTS(geom, SRID=4326;POINT(${lng} ${lat}))`;
  const url = new URL(WFS_ENDPOINT);
  url.searchParams.set("SERVICE", "WFS");
  url.searchParams.set("VERSION", "2.0.0");
  url.searchParams.set("REQUEST", "GetFeature");
  url.searchParams.set("TYPENAMES", layer);
  url.searchParams.set("CQL_FILTER", cql);
  url.searchParams.set("OUTPUTFORMAT", "application/json");
  url.searchParams.set("COUNT", "1");

  const res = await fetch(url.toString(), {
    headers: { Accept: "application/json" },
    signal: AbortSignal.timeout(15_000),
  });
  if (!res.ok) {
    throw new Error(`WFS Filosofi ${scale} HTTP ${res.status}`);
  }
  const body = (await res.json()) as WfsResponse;
  const f = body.features?.[0];
  if (!f) return null;
  return parseFeature(scale, f.properties ?? {});
}

function parseFeature(
  scale: FilosofiScale,
  p: Record<string, unknown>,
): FilosofiData {
  return {
    idInspire: asStr(p.id_inspire) ?? "",
    scale,
    depcom: asStr(p.depcom) ?? "",
    nomCommune: asStr(p.nom_com) ?? "",
    estimated: asNum(p.i_car_est) === 1,
    individus: asNum(p.ind) ?? 0,
    menages: asNum(p.men) ?? 0,
    niveauDeVieMoyen: asNum(p.ind_snv_div_ind),
    tauxPauvrete: asNum(p.men_pauv_div_men),
    partMoins18: asNum(p.part_ind_0_17_div_ind),
    part65plus: asNum(p.part_ind_65p_div_ind),
    partMaisons: asNum(p.men_mais_div_men),
    partProprietaires: asNum(p.men_prop_div_men),
    partLogementsSociaux: asNum(p.part_log_soc_div_men),
    partAvant1945: asNum(p.part_log_av45_div_men),
    partApres1990: asNum(p.part_log_ap90_div_men),
    partMonoparentales: asNum(p.men_fmp_div_men),
    surfaceMoyenne: asNum(p.men_surf_div_men),
  };
}

function asStr(v: unknown): string | null {
  return typeof v === "string" && v.length > 0 ? v : null;
}
function asNum(v: unknown): number | null {
  if (typeof v === "number") return Number.isFinite(v) ? v : null;
  if (typeof v === "string" && v.length > 0) {
    const n = Number(v);
    return Number.isFinite(n) ? n : null;
  }
  return null;
}

export async function fetchFilosofi(
  lat: number,
  lng: number,
): Promise<FilosofiData | null> {
  // Cascade 200m → 1km. Pas de fallback commune : si même la maille 1km
  // ne renvoie rien, c'est qu'on est dans une zone non habitée (forêt,
  // parc), et un agrégat commune n'aurait aucun sens local.
  const r200 = await queryFilosofi("200m", lat, lng);
  if (r200) return r200;
  const r1k = await queryFilosofi("1km", lat, lng);
  return r1k;
}

// ─── Hook ────────────────────────────────────────────────────────────────

export function useFilosofi(fiche: FicheDoc | null) {
  const opts = useMemo(() => {
    if (!fiche?.geocode || !fiche.id) return null;
    const { lat, lng } = fiche.geocode;
    return {
      ficheId: fiche.id,
      blockId: "filosofi" as const,
      // 3 décimales = ~110 m, plus large que la maille 200 m mais ça nous
      // évite de re-fetch sur de la jitter BAN. Le carreau retourné sera
      // identique tant qu'on reste dans le même bucket.
      inputsKey: `v1|${coordKey(lat, lng)}`,
      ttlDays: FILOSOFI_TTL_DAYS,
      fetcher: () => fetchFilosofi(lat, lng),
    };
  }, [fiche?.id, fiche?.geocode]);

  return useEnvBlock<FilosofiData | null>(opts);
}

// ─── Constantes pour comparaison France ──────────────────────────────────
// Référence INSEE 2021 — affichées dans la card pour situer le quartier
// vs. la moyenne nationale. À mettre à jour au prochain millésime.

export const FRANCE_REF = {
  niveauDeVieMoyen: 25_000, // €/an métropole, niveau de vie moyen 2021
  niveauDeVieMedian: 23_160, // €/an métropole, niveau de vie médian 2021
  tauxPauvrete: 14.5, // %
  partProprietaires: 57.8, // %
  partLogementsSociaux: 17, // % du parc locatif (~10 % du total)
  partMoins18: 21.5, // %
  part65plus: 20.7, // %
  partMaisons: 55, // %
} as const;
